"use client";

import { AddressRepo } from "@/core/repos/address.repo";
import container from "@/lib/di/container";
import { useState, useEffect, Suspense } from "react";
import { DetailModal } from "./modals/DetailModal";
// import { useRouter } from "next/navigation";
import { Button, Dropdown, message, Popconfirm, Tooltip } from "antd";
import {
  AccountBookOutlined,
  DashboardOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  MoreOutlined,
  PayCircleOutlined,
  PictureOutlined,
  PlusOutlined,
  FileDoneOutlined,
  SolutionOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  ContractModel,
  ContractModelFactory,
} from "@/core/models/contracts/contract.model";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import {
  contractStatusPipe,
  contractStatusTagsPipe,
} from "@/core/pipes/contract-status.pipe";
import { WaterUserDepositStatus, WaterUserStatus } from "@/core/enums/global.enums";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { usePermissionStore } from "@/core/stores/permission.store";
import dayjs from "dayjs";
import {
  FilterInputTemplate,
  FilterInputType,
  handleAddressChange,
  FilterBar,
} from "@/app/home/components/filter/FilterBar";
import {
  DataTable,
  FilterSearchPayload,
} from "@/app/home/components/table/DataTable";
import { DeleteButton } from "@/app/home/components/table/DeleteButton";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { useNavigate, useSearchParams } from "react-router";
import { depositStatusPipe } from "@/core/pipes/deposit-status.pipe";
import AuthRepo from "@/core/repos/auth.repo";
import { PERMISSIONS } from "@/core/enums/permissions.enum";

const contractRepo = container.get(ContractRepo);
const addressRepo = container.get(AddressRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const unitTypeRepo = container.get(UnitTypeRepo);
const authRepo = container.get(AuthRepo);

function Contracts() {
  const titleData = useTitleDataStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claims = authRepo.useClaims();
  const tollAreaId = searchParams.get("tollAreaId");
  const [modalData, setModalData] = useState<ContractModel>(
    ContractModelFactory.getEmpty()
  );
  const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(
    new Map(
      Object.entries({
        ...(tollAreaId
          ? { tollAreaId: { key: "tollAreaId", value: tollAreaId } }
          : ""),
      })
    )
  );
  const [selectedRows, setSelectedRows] = useState<ContractModel[]>([]);
  const [isDetailModal, setDetailModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>(
    [
      {
          dataIndex: "search",
          label: "Tên/ Mã hợp đồng",
          placeholder: "Nhập Tên/ Mã hợp đồng",
          type: FilterInputType.text,
      },
      {
          dataIndex: "note",
          label: "Ghi chú",
          placeholder: "Nhập ghi chú",
          type: FilterInputType.text,
      },
      {
          dataIndex: "meterCode",
          label: "Mã đồng hồ",
          placeholder: "Nhập mã đồng hồ",
          type: FilterInputType.text,
      },
      {
          dataIndex: "phone",
          label: "Số điện thoại",
          placeholder: "Nhập số điện thoại",
          type: FilterInputType.text,
      },
      {
          dataIndex: "identityNo",
          label: "CCCD",
          placeholder: "Nhập CCCD",
          type: FilterInputType.text,
      },
      {
          dataIndex: "hasMeter",
          label: "Trạng thái lắp đồng hồ",
          placeholder: "Chọn Trạng thái lắp đồng hồ",
          type: FilterInputType.select,
          options: [
              {
              name: "Có đồng hồ",
              value: true,
              },
              {
              name: "Chưa có đồng hồ",
              value: false,
              },
          ],
      },
      {
          dataIndex: "status",
          label: "Trạng thái hoạt động",
          placeholder: "Chọn Trạng thái hoạt động",
          type: FilterInputType.select,
          options: [
              {
              name: contractStatusPipe(WaterUserStatus.Processing),
              value: WaterUserStatus.Processing,
              },
              {
              name: contractStatusPipe(WaterUserStatus.Active),
              value: WaterUserStatus.Active,
              },
              {
              name: contractStatusPipe(WaterUserStatus.TempDisabled),
              value: WaterUserStatus.TempDisabled,
              },
              {
              name: contractStatusPipe(WaterUserStatus.Disabled),
              value: WaterUserStatus.Disabled,
              },
          ],
      },
      {
          dataIndex: "tollAreaId",
          label: "Khu vực",
          placeholder: "Chọn Khu vực",
          type: FilterInputType.select,
          options: [],
      },
      {
          dataIndex: "unitTypeId",
          label: "Loại hình đơn vị",
          placeholder: "Chọn Loại hình đơn vị",
          type: FilterInputType.select,
          options: [],
      },
      {
          dataIndex: "provinceId",
          label: "Tỉnh/ TP",
          placeholder: "Chọn Tỉnh/ TP",
          type: FilterInputType.select,
          options: [],
      },
      {
          dataIndex: "districtId",
          label: "Quận/ Huyện",
          placeholder: "Chọn Quận/ Huyện",
          type: FilterInputType.select,
          options: [],
      },
      {
          dataIndex: "wardId",
          label: "Xã/ Phường",
          placeholder: "Chọn Xã/ Phường",
          type: FilterInputType.select,
          options: [],
      },
      {
          dataIndex: "depositStatus",
          label: "Trạng thái thu cọc",
          placeholder: "Chọn trạng thái thu cọc",
          type: FilterInputType.select,
          options: Object.keys(WaterUserDepositStatus).map((key) => ({name: depositStatusPipe(key), value: key})),
      },
    ]
  );

  //* States for function buttons */
  const [isExportingAgreement, setExportingAgreement] = useState(false);
  const [isExportingContract, setExportingContract] = useState(false);
  const [isExportingContractByTollArea, setExportingContractByTollArea] =
    useState(false);

  //componentDidMount equivalent, run exclusively on client-side
  useEffect(() => {
    init();
  }, []);

  const populateSelections = async () => {
    const provinces = (await addressRepo.getAllProvinces()).map((data) => ({
      name: data[1].name,
      value: data[0],
    }));
    const tollAreas = (await tollAreaRepo.getAll()).map((area) => ({
      name: area.name,
      value: area.id,
    }));
    const unitTypes = (await unitTypeRepo.getAll()).map((unitType) => ({
      name: unitType.name,
      value: unitType.id,
    }));
    const templates: FilterInputTemplate[] = [...filterTemplates];
    const provinceTemplate = templates.find(
      (template) => template.dataIndex === "provinceId"
    );
    if (provinceTemplate) {
      provinceTemplate.options = provinces;
    }
    const tollAreaTemplate = templates.find(
      (template) => template.dataIndex === "tollAreaId"
    );
    if (tollAreaTemplate) {
      tollAreaTemplate.options = tollAreas;
    }
    const unitTypeTemplate = templates.find(
      (template) => template.dataIndex === "unitTypeId"
    );
    if (unitTypeTemplate) {
      unitTypeTemplate.options = unitTypes;
    }
    setFilterTemplates(templates);
  };

  const init = async () => {
    titleData.setTitleActions({
      text: "Thêm hợp đồng",
      onPress: () => {
        showDetail(ContractModelFactory.getEmpty());
      },
    });
    if (tollAreaId) {
      titleData.setTitleBack(true);
    }
    await populateSelections();
  };

  async function onFilterChange(
    map: Map<string, FilterSearchPayload>,
    changes: string[]
  ) {
    await handleAddressChange(
      map,
      changes,
      filterTemplates,
      setFilterTemplates
    );
    setFilterMap(new Map(map));
  }

  function onRowSelect(rows: ContractModel[]) {
    setSelectedRows([...rows]);
  }

  const deleteSelected = async () => {
    setDeleting(true);
    const result = await contractRepo.deleteMany(selectedRows);
    messageApi.success({
      content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`,
    });
    setDeleting(false);
  };

  const showDetail = (record: ContractModel) => {
    setModalData(record);
    setDetailModal(true);
  };

  const showTransactions = (record: ContractModel) => {
    navigate(
      encodeURI(
        `/billing/transaction-history?recordId=${record.id}&objectType=WaterUser`
      )
    );
  };

  const showReports = () => {
    const params = GlobalUtils.buildQueryParamsFromFilterMap(filterMap, {type: 1});
    navigate(encodeURI(`/customer/customer-report?${params}`));
  };

  const showWaterIndex = (record: ContractModel) => {
    navigate(encodeURI(`/meter-reading/water-index?waterUserId=${record.id}`));
  };

  const showInvoice = (record: ContractModel) => {
    navigate(encodeURI(`/billing/billing-table?waterUserId=${record.id}`));
  };

  const showLogs = (record: ContractModel) => {
    navigate(encodeURI(`/admin/logs?search=${record.id}&objectType=WaterUser`));
  };

  //* Callbacks for function buttons */

  const exportAgreements = async () => {
    setExportingAgreement(true);
    const rows = [...selectedRows];
    const promises = rows.map(async (record) =>
      contractRepo.exportAggreement(record.id)
    );
    const results = await Promise.all(promises);
    const successes: { filename: string; data: string }[] = [];
    results.forEach((data, index) => {
      if (data) {
        const record = rows[index];
        const filename = `BienBanThoaThuan-${record.code}-${
          record.name
        }-${dayjs().format("DDMMYYYY-hhmm")}.docx`;
        successes.push({ filename, data });
      }
    });
    if (successes.length === 0) {
      message.error(`Xuất biên bản thỏa thuận thất bại. 0/${results.length}`);
    } else {
      message.success(
        `Xuất biên bản thỏa thuận thành công. ${successes.length}/${results.length}`
      );
      for (const file of successes) {
        GlobalUtils.downloadDOCX(file.data, file.filename);
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
    setExportingAgreement(false);
  };

  const exportContracts = async () => {
    setExportingContract(true);
    const rows = [...selectedRows];
    const promises = rows.map(async (record) =>
      contractRepo.exportContract(record.id)
    );
    const results = await Promise.all(promises);
    const successes: { filename: string; data: string }[] = [];
    results.forEach((data, index) => {
      if (data) {
        const record = rows[index];
        const filename = `HopDong-${record.code}-${
          record.name
        }-${dayjs().format("DDMMYYYY-hhmm")}.docx`;
        successes.push({ filename, data });
      }
    });
    if (successes.length === 0) {
      message.error(`Xuất hợp đồng thất bại. 0/${results.length}`);
    } else {
      message.success(
        `Xuất hợp đồng thành công. ${successes.length}/${results.length}`
      );
      for (const file of successes) {
        GlobalUtils.downloadDOCX(file.data, file.filename);
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
    setExportingContract(false);
  };
  const exportContractsByTollArea = async () => {
    setExportingContractByTollArea(true);
    const tollAreaId = filterMap.get("tollAreaId")?.value;
    if (tollAreaId) {
      const data = await contractRepo.exportContractByTollArea(tollAreaId);
      if (data) {
        GlobalUtils.downloadDOCX(
          data,
          `HopDong-${dayjs().format("DDMMYYYY-hhmm")}.docx`
        );
      } else {
        message.error("Xuất hợp đồng thất bại");
      }
    } else {
      message.error("Vui lòng chọn khu vực để xuất hợp đồng");
    }
    setExportingContractByTollArea(false);
  };

  return (
    <div className="px-4 py-6">
      {contextHolder}
      <FilterBar
        setMap={setFilterMap}
        onChange={onFilterChange}
        templates={filterTemplates}
        map={filterMap}
        actions={[
          <div key={1} className="col-span-4">
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              style={{ background: "#29A339" }}
              onClick={showReports}
            >
              Xuất Excel
            </Button>
            <Button
              loading={isExportingAgreement}
              className="ml-4"
              type="primary"
              icon={<FileDoneOutlined />}
              onClick={exportAgreements}
              disabled={selectedRows.length === 0}
              style={{
                background:
                  selectedRows.length > 0 ? "var(--primary-button)" : "white",
              }}
            >
              Xuất BB Thỏa thuận
            </Button>
            <Button
              loading={isExportingContract}
              className="ml-4"
              type="primary"
              icon={<SolutionOutlined />}
              onClick={exportContracts}
              disabled={selectedRows.length === 0}
              style={{
                background:
                  selectedRows.length > 0 ? "var(--primary-button)" : "white",
              }}
            >
              Xuất Hợp đồng
            </Button>
            <Button
              loading={isExportingContractByTollArea}
              className="ml-4"
              type="primary"
              icon={<FileSearchOutlined />}
              onClick={exportContractsByTollArea}
              disabled={!filterMap.get("tollAreaId")?.value}
              style={{
                background: filterMap.get("tollAreaId")?.value
                  ? "var(--primary-button)"
                  : "white",
              }}
            >
              Xuất Hợp đồng In
            </Button>
            <Button
              className="ml-4"
              type="primary"
              icon={<FileTextOutlined />}
              disabled={true}
              style={{
                background: "white",
              }}
            >
              Hợp đồng điện tử
            </Button>
            <DeleteButton
              loading={deleting}
              length={selectedRows.length}
              onConfirm={deleteSelected}
            />
          </div>,
        ]}
      />
      <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
        <div>
          <DataTable<ContractModel>
            columns={[
              { dataIndex: "code", name: "Mã HĐ" },
              { dataIndex: "name", name: "Đại diện HĐ" },
              { dataIndex: "organization", name: "Tên tổ chức" },
              { dataIndex: "userName", name: "TK đại diện" },
              { dataIndex: "tollAreaName", name: "Khu vực" },
              { dataIndex: "unitTypeName", name: "Loại hình đơn vị" },
              { dataIndex: "waterMeterCode", name: "Mã đồng hồ nước" },
              {
                dataIndex: "depositAmount",
                name: "Số tiền đã nộp",
                pipe: formatCurrencyPipe,
              },
              {
                dataIndex: "lastReadDate",
                name: "Ngày đọc cuối",
                pipe: formatDateTimePipe,
              },
              { dataIndex: "lastWaterMeterNumber", name: "Chỉ số cuối" },
              { dataIndex: 'depositStatus', name: 'Trạng thái thu cọc', pipe: depositStatusPipe },
              {
                dataIndex: "status",
                name: "Trạng thái",
                pipe: contractStatusTagsPipe,
                fixed: "right",
              },
              {
                name: "Thao tác",
                render: (_, record) => (
                  <div className="flex flex-row px-1 items-center justify-center">
                    <Tooltip title="Sửa hợp đồng">
                      <Button
                        type="text"
                        icon={<InfoCircleOutlined />}
                        onClick={() => showDetail(record)}
                      />
                    </Tooltip>
                    <Dropdown
                      menu={{
                        items: [
                          // {
                          //     key: '1',
                          //     label: <p>Ảnh</p>,
                          //     icon: <PictureOutlined />
                          // },
                          {
                            key: "2",
                            label: <p>Chỉ số</p>,
                            icon: <DashboardOutlined />,
                            onClick: () => showWaterIndex(record),
                          },
                          {
                            key: "3",
                            label: <p>Hoá đơn</p>,
                            icon: <AccountBookOutlined />,
                            onClick: () => showInvoice(record),
                          },
                          {
                            key: "4",
                            label: <p>Giao dịch</p>,
                            icon: <PayCircleOutlined />,
                            onClick: () => showTransactions(record),
                          },
                          {
                            key: "5",
                            label: <p>Xem nhật ký</p>,
                            icon: <HistoryOutlined />,
                            onClick: () => showLogs(record),
                          },
                        ],
                      }}
                      trigger={["hover"]}
                    >
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                  </div>
                ),
                fixed: "right",
              },
            ]}
            repository={contractRepo}
            filterMap={filterMap}
            onSelect={onRowSelect}
          ></DataTable>
        </div>
      </div>
      <DetailModal
        isModalOpen={isDetailModal}
        setIsModalOpen={setDetailModal}
        data={modalData}
      ></DetailModal>
    </div>
  );
}

export default function ContractsPage() {
  return (
    <Suspense>
      <Contracts />
    </Suspense>
  );
}
